
import * as React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import type { AdditionalInterestItem } from 'features/dashboard/types/dashboard';

export type UseAdditionalInterestsResult = {
  additionalInterests: AdditionalInterestItem[];
  rawAdditionalInterests: ApiAdditionalInterest[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  totalCount: number;
};

export type ApiAdditionalInterest = {
  databaseId: string;
  certificateHolderName: string;
  email: string | null;
  cellPhone: string;
  phone: string;
  fax: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  zipCode: string | null;
  stateAbbreviationCode: string | null;
  additionalInsured: boolean;
  lossPayee: boolean;
  breachOfWarranty: boolean;
  mortgagee: boolean;
  coOwner: boolean;
  owner: boolean;
  leasebackOwner: boolean;
  registrant: boolean;
  lienholder: boolean;
  trustee: boolean;
  other: boolean;
  additionalInterestOtherText: string;
  loanNumber: string;
  description: string;
  generalLiability: boolean;
  automobileLiability: boolean;
  umbrellaLiability: boolean;
  garageLiability: boolean;
  garageKeepersLiability: boolean;
  cargo: boolean;
  trailerInterchange: boolean;
  otherLiability: boolean;
  otherLiability2: boolean;
  generalLiabilitySubrWvd: boolean;
  automobileLiabilitySubrWvd: boolean;
  umbrellaLiabilitySubrWvd: boolean;
  garageLiabilitySubrWvd: boolean;
  garageKeepersLiabilitySubrWvd: boolean;
  cargoSubrWvd: boolean;
  trailerInterchangeSubrWvd: boolean;
  otherLiabilitySubrWvd: boolean;
  otherLiability2SubrWvd: boolean;
  accord101: boolean;
  active: boolean;
  deactiveDate: string | null;
  policies: {
    databaseId?: string;
    number?: string;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    linesOfBusiness?: string[];
    carrierName?: string | null;
  }[];
  professionalLiabilities: { firstName?: string; lastName?: string }[];
  insuredDatabaseId: string;
  insuredEmail: string;
  insuredFirstName: string;
  insuredLastName: string;
  insuredCommercialName: string;
  createDate: string | null;
  changeDate: string | null;
  [key: string]: unknown;
};

const getAuthToken = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('igtoken');
};

function getRoleLabel(api: ApiAdditionalInterest): string {
  if (api.mortgagee) return 'mortgagee';
  if (api.additionalInsured) return 'Additional Insured';
  if (api.lossPayee) return 'Loss Payee';
  // Add more as needed
  return 'other';
}

function mapApiToAdditionalInterest(api: ApiAdditionalInterest): AdditionalInterestItem {
  const details: string[] = [];
  if (api.email) details.push(api.email);
  if (api.cellPhone) details.push(`Cell: ${api.cellPhone}`);
  if (api.phone) details.push(`Phone: ${api.phone}`);
  if (api.addressLine1) details.push(api.addressLine1);
  if (api.city && api.stateAbbreviationCode && api.zipCode) {
    details.push(`${api.city}, ${api.stateAbbreviationCode} ${api.zipCode}`);
  }
  if (api.loanNumber) details.push(`Loan #: ${api.loanNumber}`);
  // Add more details as needed

  return {
    id: api.databaseId,
    name: api.certificateHolderName,
    roleLabel: getRoleLabel(api),
    details
  };
}

export function useAdditionalInterests(
  enabled: boolean = true,
  insuredDatabaseId?: string | null,
  skip: number = 0,
  top: number = 5
): UseAdditionalInterestsResult {
  const [additionalInterests, setAdditionalInterests] = React.useState<AdditionalInterestItem[]>([]);
  const [rawAdditionalInterests, setRawAdditionalInterests] = React.useState<ApiAdditionalInterest[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(enabled);
  const [error, setError] = React.useState<string | null>(null);
  const [refetchKey, setRefetchKey] = React.useState(0);
  const navigate = useNavigate();

  const refetch = React.useCallback(() => setRefetchKey(k => k + 1), []);

  React.useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('You are not authenticated. Please sign in again.');
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchAdditionalInterests = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const ncToken = window.localStorage.getItem('NCToken') || '';
        const response = await axios.get<{ value: ApiAdditionalInterest[]; '@odata.count'?: number }>(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/policy-additional-interests?insuredDatabaseId=${encodeURIComponent(insuredDatabaseId || '')}&top=${top}&skip=${skip}`,
          {
            headers: {
              'NCToken': ncToken,
              'Authorization': 'Bearer ' + token,
              'Accept': 'application/json'
            }
          }
        );

        if (!isCancelled) {
          const raw = (response.data.value || []).filter(item => item.active);
          const count = (response.data as any)['@odata.count'] ?? raw.length;
          setRawAdditionalInterests(raw);
          setTotalCount(count);
          setAdditionalInterests(raw.map(mapApiToAdditionalInterest));
          setIsLoading(false);
        }
      } catch (err: any) {
        if (!isCancelled) {
          if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
            window.localStorage.clear();
            navigate('/');
            return;
          }
          setError('Failed to load additional interests.');
          setIsLoading(false);
        }
      }
    };

    void fetchAdditionalInterests();

    return () => {
      isCancelled = true;
    };
  }, [enabled, insuredDatabaseId, navigate, refetchKey, skip, top]);

  return {
    additionalInterests,
    rawAdditionalInterests,
    isLoading,
    error,
    refetch,
    totalCount,
  };
}
